import { __assign } from "tslib";
import { DateTimePatternField } from '@phensley/cldr-types';
import { getTZC } from '../../internals/calendars/zoneutil';
import { INTERNAL_NUMBERING } from '../numbering';
import { difference } from './difference';
import { dateFields } from './fields';
import { TIME_PERIOD_FIELDS } from './interval';
import { clamp, initBaseFromUnixEpoch } from './math';
import { zoneInfoFromUTC } from './timezone';
var zeropad = function (n, w) { return INTERNAL_NUMBERING.formatString(n, false, w); };
/**
 * Implementation order, based on calendar preference data and ease of implementation.
 * https://github.com/unicode-cldr/cldr-core/blob/master/supplemental/calendarPreferenceData.json
 *
 * Complete:
 *  gregorian           - widely used worldwide
 *  persian             - primary in AF, IR
 *  japanese            - secondary in JP, based on gregorian
 *  iso8601             - based on gregorian
 *  buddhist            - primary in TH
 *
 * Next:
 *  islamic-umalqura    - primary in SA
 *  chinese             - secondary in CN, CX, HK, MO, SG, TW
 *  islamic             - secondary in many locales
 *  dangi               - secondary in KO, based on chinese
 *
 * Rest TBD
 *
 * Calendar calculations are compatible with those in the Unicode ICU project.
 */
// Indicates a null field to support computing on demand
var NULL = Number.MAX_SAFE_INTEGER;
var floor = Math.floor;
/**
 * Automatically select the best field to represent the time period.
 */
var relativeField = function (p) {
    for (var _i = 0, TIME_PERIOD_FIELDS_1 = TIME_PERIOD_FIELDS; _i < TIME_PERIOD_FIELDS_1.length; _i++) {
        var f = TIME_PERIOD_FIELDS_1[_i];
        if (p[f]) {
            return f;
        }
    }
    return 'millis';
};
var differenceFields = [
    [2 /* DateField.ERA */, DateTimePatternField.ERA],
    [4 /* DateField.YEAR */, DateTimePatternField.YEAR],
    [7 /* DateField.MONTH */, DateTimePatternField.MONTH],
    [10 /* DateField.DAY_OF_MONTH */, DateTimePatternField.DAY],
    [14 /* DateField.AM_PM */, DateTimePatternField.DAYPERIOD],
    [16 /* DateField.HOUR */, DateTimePatternField.HOUR24],
    [17 /* DateField.MINUTE */, DateTimePatternField.MINUTE],
    [18 /* DateField.SECOND */, DateTimePatternField.SECOND],
];
/**
 * Base class for dates in supported calendars.
 *
 * @public
 */
var CalendarDate = /** @class */ (function () {
    /**
     * Minimal fields required to construct any calendar date.
     */
    function CalendarDate(_type, _firstDay, _minDays) {
        this._type = _type;
        this._firstDay = _firstDay;
        this._minDays = _minDays;
        this._fields = dateFields();
        // Compute week fields on demand.
        this._fields[6 /* DateField.WEEK_OF_YEAR */] = NULL;
        this._fields[5 /* DateField.YEAR_WOY */] = NULL;
        this._zoneInfo = zoneInfoFromUTC('UTC', 0);
    }
    /**
     * Calendar type for this date, e.g. 'gregory' for Gregorian.
     *
     * @public
     */
    CalendarDate.prototype.type = function () {
        return this._type;
    };
    /**
     * Returns a formatted ISO-8601 string of the date in UTC. Note that this
     * always returns a date in the Gregorian calendar.
     *
     * @public
     */
    CalendarDate.prototype.toISOString = function () {
        return this._toISOString(this, true);
    };
    /**
     * Returns a formatted ISO 8601 string of the date with local timezone offset.
     * Note that this always returns a date in the Gregorian calendar.
     *
     * @public
     */
    CalendarDate.prototype.toLocalISOString = function () {
        return this._toISOString(this, false);
    };
    /**
     * Unix epoch with no timezone offset.
     *
     * @public
     */
    CalendarDate.prototype.unixEpoch = function () {
        return this._fields[0 /* DateField.LOCAL_MILLIS */] - this._zoneInfo.offset;
    };
    /**
     * First day of week.
     *
     * @public
     */
    CalendarDate.prototype.firstDayOfWeek = function () {
        return this._firstDay;
    };
    /**
     * Minimum days in the first week.
     *
     * @public
     */
    CalendarDate.prototype.minDaysInFirstWeek = function () {
        return this._minDays;
    };
    /**
     * Returns a floating point number representing the real Julian Day, UTC.
     *
     * @public
     */
    CalendarDate.prototype.julianDay = function () {
        var ms = (this._fields[13 /* DateField.MILLIS_IN_DAY */] - this._zoneInfo.offset) / 86400000 /* CalendarConstants.ONE_DAY_MS */;
        return this._fields[1 /* DateField.JULIAN_DAY */] - 0.5 + ms;
    };
    /**
     * CLDR's modified Julian day used as the basis for all date calculations.
     *
     * @public
     */
    CalendarDate.prototype.modifiedJulianDay = function () {
        return this._fields[1 /* DateField.JULIAN_DAY */];
    };
    /**
     * Era
     *
     * @public
     */
    CalendarDate.prototype.era = function () {
        return this._fields[2 /* DateField.ERA */];
    };
    /**
     * Extended year.
     *
     * @public
     */
    CalendarDate.prototype.extendedYear = function () {
        return this._fields[3 /* DateField.EXTENDED_YEAR */];
    };
    /**
     * Year.
     *
     * @public
     */
    CalendarDate.prototype.year = function () {
        return this._fields[4 /* DateField.YEAR */];
    };
    /**
     * Related year.
     *
     * @public
     */
    CalendarDate.prototype.relatedYear = function () {
        return this._fields[3 /* DateField.EXTENDED_YEAR */];
    };
    /**
     * Year of week of year.
     *
     * @public
     */
    CalendarDate.prototype.yearOfWeekOfYear = function () {
        this.computeWeekFields();
        return this._fields[5 /* DateField.YEAR_WOY */];
    };
    /**
     * Week of year.
     *
     * @public
     */
    CalendarDate.prototype.weekOfYear = function () {
        this.computeWeekFields();
        return this._fields[6 /* DateField.WEEK_OF_YEAR */];
    };
    /**
     * Year of week of year ISO.
     *
     * @public
     */
    CalendarDate.prototype.yearOfWeekOfYearISO = function () {
        this.computeWeekFields();
        return this._fields[23 /* DateField.ISO_YEAR_WOY */];
    };
    /**
     * Week of year ISO.
     *
     * @public
     */
    CalendarDate.prototype.weekOfYearISO = function () {
        this.computeWeekFields();
        return this._fields[24 /* DateField.ISO_WEEK_OF_YEAR */];
    };
    /**
     * Ordinal month, one-based, e.g. Gregorian JANUARY = 1.
     */
    CalendarDate.prototype.month = function () {
        return this._fields[7 /* DateField.MONTH */];
    };
    /**
     * Returns the week of the month computed using the locale's 'first day
     * of week' and 'minimal days in first week' where applicable.
     *
     * For example, for the United States, weeks start on Sunday.
     * Saturday 9/1/2018 would be in week 1, and Sunday 9/2/2018 would
     * begin week 2.
     *
     *         September
     *   Su Mo Tu We Th Fr Sa
     *                      1
     *    2  3  4  5  6  7  8
     *    9 10 11 12 13 14 15
     *   16 17 18 19 20 21 22
     *   23 24 25 26 27 28 29
     *   30
     */
    CalendarDate.prototype.weekOfMonth = function () {
        this.computeWeekFields();
        return this._fields[8 /* DateField.WEEK_OF_MONTH */];
    };
    CalendarDate.prototype.dayOfYear = function () {
        return this._fields[9 /* DateField.DAY_OF_YEAR */];
    };
    /**
     * Day of the week. 1 = SUNDAY, 2 = MONDAY, ..., 7 = SATURDAY
     */
    CalendarDate.prototype.dayOfWeek = function () {
        return this._fields[11 /* DateField.DAY_OF_WEEK */];
    };
    /**
     * Ordinal day of the week. 1 if this is the 1st day of the week,
     * 2 if the 2nd, etc. Depends on the local starting day of the week.
     */
    CalendarDate.prototype.ordinalDayOfWeek = function () {
        var weekday = this.dayOfWeek();
        var firstDay = this.firstDayOfWeek();
        return ((7 - firstDay + weekday) % 7) + 1;
    };
    /**
     * Ordinal number indicating the day of the week in the current month.
     * The result of this method can be used to format messages like
     * "2nd Sunday in August".
     */
    CalendarDate.prototype.dayOfWeekInMonth = function () {
        this.computeWeekFields();
        return this._fields[12 /* DateField.DAY_OF_WEEK_IN_MONTH */];
    };
    /**
     * Day of month.
     *
     * @public
     */
    CalendarDate.prototype.dayOfMonth = function () {
        return this._fields[10 /* DateField.DAY_OF_MONTH */];
    };
    /**
     * Is AM.
     *
     * @public
     */
    CalendarDate.prototype.isAM = function () {
        return this._fields[14 /* DateField.AM_PM */] === 0;
    };
    /**
     * Indicates the hour of the morning or afternoon, used for the 12-hour
     * clock (0 - 11). Noon and midnight are 0, not 12.
     */
    CalendarDate.prototype.hour = function () {
        return this._fields[16 /* DateField.HOUR */];
    };
    /**
     * Indicates the hour of the day, used for the 24-hour clock (0 - 23).
     * Noon is 12 and midnight is 0.
     */
    CalendarDate.prototype.hourOfDay = function () {
        return this._fields[15 /* DateField.HOUR_OF_DAY */];
    };
    /**
     * Indicates the minute of the hour (0 - 59).
     */
    CalendarDate.prototype.minute = function () {
        return this._fields[17 /* DateField.MINUTE */];
    };
    /**
     * Indicates the second of the minute (0 - 59).
     */
    CalendarDate.prototype.second = function () {
        return this._fields[18 /* DateField.SECOND */];
    };
    /**
     * Milliseconds.
     *
     * @public
     */
    CalendarDate.prototype.milliseconds = function () {
        return this._fields[19 /* DateField.MILLIS */];
    };
    /**
     * Milliseconds in day.
     *
     * @public
     */
    CalendarDate.prototype.millisecondsInDay = function () {
        return this._fields[13 /* DateField.MILLIS_IN_DAY */];
    };
    /**
     * Metazone (CLDR) identifier.
     *
     * @public
     */
    CalendarDate.prototype.metaZoneId = function () {
        return this._zoneInfo.metazoneid;
    };
    /**
     * Timezone identifier.
     *
     * @public
     */
    CalendarDate.prototype.timeZoneId = function () {
        return this._zoneInfo.zoneid;
    };
    /**
     * Timezone stable identifier (CLDR)
     *
     * @public
     */
    CalendarDate.prototype.timeZoneStableId = function () {
        return this._zoneInfo.stableid;
    };
    /**
     * Timezone offset
     *
     * @public
     */
    CalendarDate.prototype.timeZoneOffset = function () {
        return this._zoneInfo.offset;
    };
    /**
     * Is leap year.
     *
     * @public
     */
    CalendarDate.prototype.isLeapYear = function () {
        return this._fields[21 /* DateField.IS_LEAP */] === 1;
    };
    /**
     * Is daylight savings time.
     *
     * @public
     */
    CalendarDate.prototype.isDaylightSavings = function () {
        return this._zoneInfo.dst === 1;
    };
    /**
     * Computes the field of visual difference between the two dates.
     * Note: This assumes the dates are of the same type and have the same
     * timezone offset.
     *
     * @public
     */
    CalendarDate.prototype.fieldOfVisualDifference = function (other) {
        var a = this._fields;
        var b = other._fields;
        for (var _i = 0, differenceFields_1 = differenceFields; _i < differenceFields_1.length; _i++) {
            var _c = differenceFields_1[_i], key = _c[0], field = _c[1];
            if (a[key] !== b[key]) {
                return field;
            }
        }
        return undefined;
    };
    /**
     * Compare two dates a and b, returning:
     *
     * ```
     *   a < b  ->  -1
     *   a = b  ->  0
     *   a > b  ->  1
     * ```
     *
     * @public
     */
    CalendarDate.prototype.compare = function (other) {
        return compare(this, other);
    };
    /**
     * Calculate the relative time between two dates. If a field is specified
     * the time will be calculated in terms of that single field. Otherwise
     * the field will be auto-selected.
     *
     * @public
     */
    CalendarDate.prototype.relativeTime = function (other, field, options) {
        options = options || {};
        var result;
        // If no field is specified, calculate the difference in all fields and select the largest.
        if (field === undefined) {
            result = difference(this, other, __assign(__assign({}, options), { rollupFractional: false, fields: undefined }));
            field = relativeField(result);
        }
        // Calculate the difference in terms of the requested or auto-selected field.
        options = __assign(__assign({}, options), { fields: [field] });
        result = difference(this, other, options);
        return [field, result[field] || 0];
    };
    /**
     * Calculate the period of time (years, months, days, ..) since the given date.
     *
     * In a normal call, "other is less-than-or-equal-to this".
     * If "this is less-than other" we invert the sign of the result.
     *
     * For example:
     *
     *   "2024-03-03".since("2024-01-30") == "1 month 4 days"
     *   "2024-01-30".since("2024-03-03") == "-1 month -3 days"
     *
     * The durations are not symmetrical due to calendar math, e.g. end-of-month alignment
     * with months of different lengths.
     *
     * @public
     */
    CalendarDate.prototype.since = function (other, options) {
        return this._negatePeriod(difference(this, other, options || {}));
    };
    /**
     * Calculate the period of time (years, months, days, ..) until the given date.
     *
     * In a normal call, "this is less-than-or-equal-to other".
     * If "other is less-than this" we invert the sign of the result.
     *
     * For example:
     *
     *   "2024-01-30".until("2024-03-30") == "1 month 3 days"
     *   "2024-03-03".until("2024-01-30") == "-1 month -4 days"
     *
     * The duration are not symmetrical due to calendar math, e.g. end-of-month alignment
     * with months of different lengths.
     *
     * @public
     */
    CalendarDate.prototype.until = function (other, options) {
        return difference(this, other, options || {});
    };
    /**
     * Calculate the time period between two dates. Note this returns the
     * absolute value of the difference.
     *
     * @public
     */
    CalendarDate.prototype.difference = function (other, options) {
        var result = difference(this, other, options || {});
        return this.compare(other) === 1 ? this._negatePeriod(result) : result;
    };
    /**
     * Calculate the time period between two dates. If 'other' is before this date,
     * the time period fields will be negative.
     *
     * @public
     */
    CalendarDate.prototype.differenceSigned = function (other, options) {
        return this.until(other, options);
    };
    /**
     * Return all of the date and time field values.
     *
     * @public
     */
    CalendarDate.prototype.fields = function () {
        return {
            year: this.extendedYear(),
            month: this.month(),
            day: this.dayOfMonth(),
            hour: this.hourOfDay(),
            minute: this.minute(),
            second: this.second(),
            millis: this.milliseconds(),
            zoneId: this.timeZoneId(),
        };
    };
    /**
     * Return a JavaScript Date object with the same date and time.
     *
     * @public
     */
    CalendarDate.prototype.asJSDate = function () {
        return new Date(this.toLocalISOString());
    };
    /**
     * Return a string containing the date.
     */
    CalendarDate.prototype.toDateString = function () {
        var y = this.extendedYear();
        var neg = y < 0;
        return ("".concat(neg ? '-' : '').concat(zeropad(Math.abs(y), 4)) + "-".concat(zeropad(this.month(), 2), "-").concat(zeropad(this.dayOfMonth(), 2)));
    };
    /**
     * Return a string containing the time, with optional timezone.
     */
    CalendarDate.prototype.toTimeString = function (options) {
        var s = "".concat(zeropad(this.hourOfDay(), 2), ":").concat(zeropad(this.minute(), 2), ":").concat(zeropad(this.second(), 2));
        var ms = this.milliseconds();
        // Check if user disabled display of milliseconds when zero
        var showMilliseconds = !(options && options.optionalMilliseconds && ms === 0);
        if (showMilliseconds) {
            s += ".".concat(zeropad(ms, 3));
        }
        if (options) {
            if (options.includeZoneOffset) {
                s += "".concat(formatZoneOffset(this));
            }
            if (options.includeZoneId) {
                s += " ".concat(this._zoneInfo.zoneid);
            }
        }
        return s;
    };
    /**
     * Return a compact string containing just date and time, with optional timezone.
     */
    CalendarDate.prototype.toDateTimeString = function (options) {
        return "".concat(this.toDateString(), " ").concat(this.toTimeString(options));
    };
    /**
     * Start of date, e.g. `date.startOf('day')`
     *
     * @public
     */
    CalendarDate.prototype.startOf = function (field) {
        switch (field) {
            case 'year':
                return this.set({
                    month: 0,
                    day: 0,
                    hour: 0,
                    minute: 0,
                    second: 0,
                    millis: 0,
                });
            case 'month':
                return this.set({ day: 0, hour: 0, minute: 0, second: 0, millis: 0 });
            case 'day':
                return this.set({ hour: 0, minute: 0, second: 0, millis: 0 });
            case 'hour':
                return this.set({ minute: 0, second: 0, millis: 0 });
            case 'minute':
                return this.set({ second: 0, millis: 0 });
            case 'second':
                return this.set({ millis: 0 });
        }
    };
    /**
     * End of date, e.g. `date.endOf('day')`
     *
     * @public
     */
    CalendarDate.prototype.endOf = function (field) {
        switch (field) {
            case 'year':
                return this.set({
                    month: 13,
                    day: 32,
                    hour: 23,
                    minute: 59,
                    second: 59,
                    millis: 999,
                });
            case 'month':
                return this.set({
                    day: 32,
                    hour: 23,
                    minute: 59,
                    second: 59,
                    millis: 999,
                });
            case 'day':
                return this.set({ hour: 23, minute: 59, second: 59, millis: 999 });
            case 'hour':
                return this.set({ minute: 59, second: 59, millis: 999 });
            case 'minute':
                return this.set({ second: 59, millis: 999 });
            case 'second':
                return this.set({ millis: 999 });
        }
    };
    CalendarDate.prototype._toISOString = function (d, utc) {
        d = CalendarDate._gregorian(this, utc, this._firstDay, this._minDays);
        var z = 'Z';
        if (!utc) {
            var o = (this.timeZoneOffset() / 60000 /* CalendarConstants.ONE_MINUTE_MS */) | 0;
            z = "".concat(o < 0 ? '-' : '+').concat(zeropad((o / 60) | 0, 2), ":").concat(zeropad((o % 60) | 0, 2));
        }
        var y = d.extendedYear();
        var neg = y < 0;
        return ("".concat(neg ? '-' : '').concat(zeropad(Math.abs(y), 4), "-").concat(zeropad(d.month(), 2), "-").concat(zeropad(d.dayOfMonth(), 2)) +
            "T".concat(zeropad(d.hourOfDay(), 2), ":").concat(zeropad(d.minute(), 2), ":").concat(zeropad(d.second(), 2)) +
            ".".concat(zeropad(d.milliseconds(), 3)).concat(z));
    };
    /**
     * Rollup just the time fields into number of milliseconds. This is internal
     * and assumes all time fields are defined.
     */
    CalendarDate.prototype._timeToMs = function (f) {
        return ((clamp(f.hour || 0, 0, 23) | 0) * 3600000 /* CalendarConstants.ONE_HOUR_MS */ +
            (clamp(f.minute || 0, 0, 59) | 0) * 60000 /* CalendarConstants.ONE_MINUTE_MS */ +
            (clamp(f.second || 0, 0, 59) | 0) * 1000 /* CalendarConstants.ONE_SECOND_MS */ +
            (clamp(f.millis || 0, 0, 999) | 0));
    };
    CalendarDate.prototype._negatePeriod = function (fields) {
        var r = {};
        for (var _i = 0, TIME_PERIOD_FIELDS_2 = TIME_PERIOD_FIELDS; _i < TIME_PERIOD_FIELDS_2.length; _i++) {
            var f = TIME_PERIOD_FIELDS_2[_i];
            var v = fields[f];
            r[f] = v ? -v : 0;
        }
        return r;
        // const result: Partial<TimePeriod> = {};
        // (Object.keys(TIME_PERIOD_FIELDS) as (keyof TimePeriod)[]).forEach((k) => (result[k] = -(fields[k] || 0)));
        // return result as TimePeriod;
    };
    CalendarDate.prototype.initFromUnixEpoch = function (ms, zoneId) {
        this._zoneInfo = initBaseFromUnixEpoch(this._fields, ms, zoneId);
    };
    CalendarDate.prototype.initFromJD = function (jd, msDay, zoneId) {
        var unixEpoch = unixEpochFromJD(jd, msDay);
        this.initFromUnixEpoch(unixEpoch, zoneId);
    };
    CalendarDate.prototype._toString = function (type) {
        return "".concat(type, " ").concat(this.toDateString(), " ").concat(this.toTimeString({ includeZoneId: true }));
    };
    /**
     * Compute WEEK_OF_YEAR and YEAR_WOY on demand.
     */
    CalendarDate.prototype.computeWeekFields = function () {
        var f = this._fields;
        if (f[5 /* DateField.YEAR_WOY */] !== NULL) {
            return;
        }
        var dow = f[11 /* DateField.DAY_OF_WEEK */];
        var dom = f[10 /* DateField.DAY_OF_MONTH */];
        var doy = f[9 /* DateField.DAY_OF_YEAR */];
        f[8 /* DateField.WEEK_OF_MONTH */] = this.weekNumber(this._firstDay, this._minDays, dom, dom, dow);
        f[12 /* DateField.DAY_OF_WEEK_IN_MONTH */] = (((dom - 1) / 7) | 0) + 1;
        // compute locale
        this._computeWeekFields(6 /* DateField.WEEK_OF_YEAR */, 5 /* DateField.YEAR_WOY */, this._firstDay, this._minDays, dow, dom, doy);
        // compute ISO
        this._computeWeekFields(24 /* DateField.ISO_WEEK_OF_YEAR */, 23 /* DateField.ISO_YEAR_WOY */, 2, 4, dow, dom, doy);
    };
    CalendarDate.prototype._computeWeekFields = function (woyfield, ywoyfield, firstDay, minDays, dow, _dom, doy) {
        var f = this._fields;
        var eyear = f[3 /* DateField.EXTENDED_YEAR */];
        var ywoy = eyear;
        var rdow = (dow + 7 - firstDay) % 7;
        var rdowJan1 = (dow - doy + 7001 - firstDay) % 7;
        var woy = floor((doy - 1 + rdowJan1) / 7);
        if (7 - rdowJan1 >= minDays) {
            woy++;
        }
        if (woy === 0) {
            var prevDay = doy + this.yearLength(eyear - 1);
            woy = this.weekNumber(firstDay, minDays, prevDay, prevDay, dow);
            ywoy--;
        }
        else {
            var lastDoy = this.yearLength(eyear);
            if (doy >= lastDoy - 5) {
                var lastRdow = (rdow + lastDoy - doy) % 7;
                if (lastRdow < 0) {
                    lastRdow += 7;
                }
                if (6 - lastRdow >= minDays && doy + 7 - rdow > lastDoy) {
                    woy = 1;
                    ywoy++;
                }
            }
        }
        f[woyfield] = woy;
        f[ywoyfield] = ywoy;
    };
    CalendarDate.prototype.yearLength = function (y) {
        return this.monthStart(y + 1, 0, false) - this.monthStart(y, 0, false);
    };
    CalendarDate.prototype.weekNumber = function (firstDay, minDays, desiredDay, dayOfPeriod, dayOfWeek) {
        var psow = (dayOfWeek - firstDay - dayOfPeriod + 1) % 7;
        if (psow < 0) {
            psow += 7;
        }
        var weekNo = floor((desiredDay + psow - 1) / 7);
        return 7 - psow >= minDays ? weekNo + 1 : weekNo;
    };
    return CalendarDate;
}());
export { CalendarDate };
// TODO: clamp range instead of throwing error.
// const checkJDRange = (jd: number): void => {
//   if (jd < CalendarConstants.JD_MIN || jd > CalendarConstants.JD_MAX) {
//     throw new Error(
//       `Julian day ${jd} is outside the supported range of this library: ` +
//         `${ConstantsDesc.JD_MIN} to ${ConstantsDesc.JD_MAX}`,
//     );
//   }
// };
/**
 * Given a Julian day and local milliseconds (in UTC), return the Unix
 * epoch milliseconds UTC.
 */
var unixEpochFromJD = function (jd, msDay) {
    var days = jd - 2440588 /* CalendarConstants.JD_UNIX_EPOCH */;
    return days * 86400000 /* CalendarConstants.ONE_DAY_MS */ + Math.round(msDay);
};
var formatZoneOffset = function (date) {
    var _c = getTZC(date.timeZoneOffset()), _ = _c[0], negative = _c[1], hours = _c[2], minutes = _c[3];
    var s = '';
    var zero = hours === 0 && minutes === 0;
    s += zero ? '+' : negative ? '-' : '+';
    s += zeropad(hours, 2);
    s += ':';
    s += zeropad(minutes, 2);
    return s;
};
/**
 * Compare two dates a and b, returning:
 *
 * ```
 *   a < b  ->  -1
 *   a = b  ->  0
 *   a > b  ->  1
 * ```
 */
export var compare = function (a, b) {
    var _a = a.unixEpoch();
    var _b = b.unixEpoch();
    return _a < _b ? -1 : _a > _b ? 1 : 0;
};
//# sourceMappingURL=calendar.js.map